import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";

import type { Destination } from "@/shared/types";
import type { IDTypes } from "@/portals/customer/hooks/useIDTypes";

export enum PaymentAdjustedByAgeOperation {
  ADD = "add",
  SUBTRACTION = "subtraction",
  MULTIPLY = "multiply",
  DIVISION = "division",
}

export type AppStoreActions = {
  setAllCountries: (destinations: Destination[]) => void;
  setAllLocalDestinations: (destinations: Destination[]) => void;
  getAreaCoverageByDestination: (name: string, isDomestic: boolean) => string[];
  setPaymentPrice: (payload: {
    currencyCode: string;
    rateAmount: number;
    currencyId: number;
  }) => void;
  setPaymentConvertedPrice: (payload?: {
    currencyCode: string;
    rateAmount: number;
    currencyId: number;
  }) => void;
  setAdjustmentPriceByAge: (rateAmount: number) => void;
  setValidIds: (payload: IDTypes[]) => void;
  setTempDataId: (payload: string) => void;
  setCoverageType: (value: string) => void;
};

export type AppStoreState = {
  tempDataId: string;
  countries: Destination[];
  localDestinations: Destination[];
  payment: {
    isForexConverted: boolean;
    currencyCode: string;
    rateAmount: number;
    currencyId: number;
    converted?: {
      currencyCode: string;
      rateAmount: number;
      currencyId: number;
    };
    adjustedPriceByAge?: {
      currencyCode: string;
      rateAmount: number;
      currencyId: number;
    };
  };
  validIdList: IDTypes[];
  coverageType?: string;
};

export type ProductPlanStore = AppStoreState & AppStoreActions;

export const initialState: AppStoreState = {
  tempDataId: "",
  countries: [],
  localDestinations: [],
  validIdList: [],
  payment: {
    isForexConverted: false,
    currencyCode: "PHP",
    rateAmount: 0,
    currencyId: 1,
  },
  coverageType: "",
};

export const useAppStore = create<ProductPlanStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        setAllCountries: (newDestinations: Destination[]) =>
          set({ countries: newDestinations }),
        setAllLocalDestinations: (newDestinations: Destination[]) =>
          set({ localDestinations: newDestinations }),
        setPaymentPrice: (newValue) => {
          const { payment } = get();
          set({
            payment: {
              ...payment,
              currencyCode: newValue.currencyCode,
              rateAmount: newValue.rateAmount,
              currencyId: newValue.currencyId,
            },
          });
        },
        setPaymentConvertedPrice: (newValue) => {
          const { payment } = get();
          const isForexConverted = !!newValue?.currencyId;
          set({
            payment: {
              ...payment,
              converted: newValue,
              isForexConverted,
            },
          });
        },
        setAdjustmentPriceByAge: (adjustedRate: number) => {
          const { payment } = get();
          set({
            payment: {
              ...payment,
              adjustedPriceByAge:
                adjustedRate > 0
                  ? {
                      currencyCode: payment.currencyCode,
                      rateAmount: adjustedRate,
                      currencyId: payment.currencyId,
                    }
                  : undefined,
            },
          });
        },
        getAreaCoverageByDestination: (
          name: string,
          isDomestic: boolean,
        ): string[] => {
          const { countries, localDestinations } = get();

          const destinations = isDomestic ? localDestinations : countries;
          const foundDestination = destinations.find(
            (destination) => destination.destinationName === name,
          );

          return (
            foundDestination?.areaCoverage || ["No area coverage available"]
          );
        },
        setValidIds: (newValue): void => {
          set({
            validIdList: newValue,
          });
        },
        setTempDataId: (tempDataId): void => {
          set({
            tempDataId,
          });
        },
        setCoverageType: (value: string) =>
          set({ coverageType: value })
      }),
      {
        name: "app-store",
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({
          payment: state.payment,
          validIdList: state.validIdList,
          tempDataId: state.tempDataId,
          coverageType: state.coverageType,
        }),
      },
    ),
    { name: "AppStore" },
  ),
);
